import { ApiShopperLandingPageLink } from '@storefronts/common-types'
import Image from 'next/image'
import { useContext, useEffect, useMemo, useState } from 'react'
import { AnalyticsContext } from '../../../context/analyticsContext'
import { PageContext } from '../../../context/PageContextProvider'
import { ViddyCoreContext } from '../../../context/viddyCoreContext'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import { VideoContext } from '../../../context/viddyVideoContext'
import {
    DiscountBadge,
    ListPrice,
    PriceDollars,
} from '../../shared/ProductCard'
import { ReviewsStars } from '../reviews/Reviews'
import { getReviewSummary } from '../../../utils/getReviewSummary'
import { getLinkDefaultVariantImageUrl } from '../../../utils/helpers'
import {
    getLinkPrices,
    formatPriceWithoutSymbol,
    splitDollarsAndCents,
} from '../../../utils/priceFormat'
import {
    BottomControlsContainer,
    BottomControlsOuterContainer,
    LPBottomControlsDiv,
} from './Shared'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import React from 'react'
import { getTypographyRoleStyle } from '../../../style/typography'
import {
    VerifiedBadgeIcon,
    MutedSpeakerIcon,
    UnmutedSpeakerIcon,
} from '../../icons/Icons'
import { useBenefitsTexts } from '../../../hooks/useBenefitsTexts'
import { scaledFontSize } from '../../../style/scrollingLayout'

// Fade animation for rotating benefits
const fadeIn = keyframes`
    0% { opacity: 0; transform: translateY(4px); }
    15% { opacity: 1; transform: translateY(0); }
    85% { opacity: 1; transform: translateY(0); }
    100% { opacity: 0; transform: translateY(-4px); }
`

const RotatingBenefitText = styled.span<{ duration: number }>(
    {
        display: 'inline-block',
        ...getTypographyRoleStyle('promoText', {
            fontWeight: 400,
            lineHeight: 1.3,
        }),
        fontSize: scaledFontSize(12),
        color: '#ffffff',
        opacity: 0.8,
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
    },
    ({ duration }) => ({
        animation: `${fadeIn} ${duration}s ease-in-out infinite`,
    }),
)

type BottomControlsProps = {
    lpLink?: ApiShopperLandingPageLink | null
}

// eslint-disable-next-line complexity
export const BottomControlsSingleVideo: React.FC<BottomControlsProps> = ({
    lpLink = null,
}) => {
    const { Analytics } = useContext(AnalyticsContext)
    const { playerVideo } = useContext(ViddyCoreContext)
    const { setActiveLPLink } = useContext(PageContext)
    const { landingPage } = useContext(ViddyLPContext)
    const { isMuted, setIsMuted, setVideoTapped } = useContext(VideoContext)
    const [isCardExpanded, setIsCardExpanded] = useState(false)
    const [thumbOrientation, setThumbOrientation] = useState<
        'unknown' | 'portrait' | 'square' | 'landscape'
    >('unknown')
    const _onPrimaryCtaClick = () => {
        if (!lpLink) return
        setActiveLPLink(lpLink)
        Analytics.TRACK_LANDING_PAGE_CTA_CLICK({
            type: 'video_primary',
            videoName: playerVideo?.name || '',
            productTitle: lpLink?.title || '',
        })
    }

    const toggleMute = () => {
        const newMutedState = !isMuted
        setIsMuted(newMutedState)
        setVideoTapped(true)
        Analytics.TRACK_VIDEO_PLAYER_MUTE_TOGGLE(
            newMutedState ? 'muted' : 'unmuted',
            'main_video_icon_click',
        )
    }

    useEffect(() => {
        // Gently slide the product card out a moment after the video starts
        const timer = setTimeout(() => {
            setIsCardExpanded(true)
        }, 1600)
        return () => clearTimeout(timer)
    }, [])

    const { dollars, cents, compareAtPrice, discountPercentage, priceInfo } =
        useMemo(() => {
            if (!landingPage || !lpLink) {
                return {
                    dollars: '',
                    cents: '',
                    compareAtPrice: null as string | null,
                    discountPercentage: 0,
                    priceInfo: {
                        price: 0,
                        compareAtPrice: null,
                        discountPercentage: null,
                    },
                }
            }

            const priceInfo = getLinkPrices(
                lpLink,
                landingPage.discount || null,
            )
            const stringPrice = formatPriceWithoutSymbol(priceInfo.price)
            const split = splitDollarsAndCents(stringPrice)

            const compare =
                priceInfo.compareAtPrice != null
                    ? formatPriceWithoutSymbol(priceInfo.compareAtPrice)
                    : null

            return {
                dollars: split.dollars,
                cents: split.cents,
                compareAtPrice: compare,
                discountPercentage: priceInfo.discountPercentage,
                priceInfo,
            }
        }, [landingPage, lpLink])

    const { reviewRating, reviewCount } =
        landingPage && lpLink
            ? getReviewSummary(landingPage, lpLink)
            : { reviewRating: 0, reviewCount: 0 }

    // Get benefits texts for rotating subtitle
    const benefitsTexts = useBenefitsTexts(
        landingPage?.bannerText,
        landingPage?.bannerBenefits || [],
        landingPage?.settings?.brandBenefits || [],
    )

    const [currentBenefitIndex, setCurrentBenefitIndex] = useState(0)
    const BENEFIT_DURATION = 3.5 // seconds per benefit

    useEffect(() => {
        if (benefitsTexts.length > 0) {
            setCurrentBenefitIndex((prev) =>
                prev >= benefitsTexts.length ? 0 : prev,
            )
        }
    }, [benefitsTexts.length])

    useEffect(() => {
        if (benefitsTexts.length <= 1) return
        const interval = setInterval(() => {
            setCurrentBenefitIndex((prev) => (prev + 1) % benefitsTexts.length)
        }, BENEFIT_DURATION * 1000)
        return () => clearInterval(interval)
    }, [benefitsTexts.length])

    if (!playerVideo) return null

    return (
        <BottomControlsOuterContainer>
            <BottomControlsContainer style={{ flexDirection: 'column' }}>
                {/* Brand row - now below product card */}
                {landingPage && (
                    <BrandRowContainer isExpanded={isCardExpanded}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            <BrandAvatarButton
                                onClick={_onPrimaryCtaClick}
                                aria-label="View product details"
                            >
                                <img
                                    src={
                                        landingPage.client.imageUrl ||
                                        landingPage.imageUrl
                                    }
                                    alt={landingPage.client.displayName}
                                    style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        objectFit: 'contain',
                                    }}
                                />
                            </BrandAvatarButton>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    minWidth: 0,
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        whiteSpace: 'nowrap',
                                        overflow: 'visible',
                                    }}
                                >
                                    <span
                                        style={{
                                            ...getTypographyRoleStyle(
                                                'navText',
                                                {
                                                    fontWeight: 500,
                                                    lineHeight: 1.3,
                                                },
                                            ),
                                            fontSize: scaledFontSize(14),
                                            color: '#ffffff',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {landingPage.client.displayName ||
                                            landingPage.title}
                                    </span>
                                    <VerifiedBadgeIcon color="#FFFFFF" />
                                </div>
                                {benefitsTexts.length > 0 ? (
                                    <RotatingBenefitText
                                        key={currentBenefitIndex}
                                        duration={BENEFIT_DURATION}
                                        style={{ marginTop: 2 }}
                                    >
                                        {
                                            benefitsTexts[
                                                currentBenefitIndex %
                                                    benefitsTexts.length
                                            ]
                                        }
                                    </RotatingBenefitText>
                                ) : landingPage.title ? (
                                    <span
                                        style={{
                                            marginTop: 2,
                                            ...getTypographyRoleStyle(
                                                'captionText',
                                                {
                                                    fontWeight: 400,
                                                    lineHeight: 1.3,
                                                },
                                            ),
                                            fontSize: scaledFontSize(12),
                                            color: '#ffffff',
                                            opacity: 0.8,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {landingPage.title}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                        <NavCircleButton
                            type="button"
                            onClick={toggleMute}
                            aria-label={isMuted ? 'Unmute' : 'Mute'}
                        >
                            {isMuted ? (
                                <MutedSpeakerIcon />
                            ) : (
                                <UnmutedSpeakerIcon />
                            )}
                        </NavCircleButton>
                    </BrandRowContainer>
                )}
                {/* Product card - now on top via flexbox order */}
                {lpLink && (
                    <LPBottomControlsDiv
                        style={{
                            paddingBottom: 5,
                            justifyContent: 'center',
                            alignItems: 'stretch',
                            pointerEvents: 'auto',
                            paddingLeft: 16,
                            paddingRight: 16,
                            boxSizing: 'border-box',
                            order: 1,
                            marginBottom: 5,
                        }}
                    >
                        <VideoProductCard
                            expanded={isCardExpanded}
                            onClick={_onPrimaryCtaClick}
                            style={{ width: '100%', flex: 'none' }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                }}
                            >
                                <div
                                    style={{
                                        width:
                                            thumbOrientation === 'landscape'
                                                ? 80
                                                : 60,
                                        height:
                                            thumbOrientation === 'landscape'
                                                ? 80
                                                : 60,
                                        position: 'relative',
                                        borderRadius: '0.2rem',
                                        overflow: 'hidden',
                                        flexShrink: 0,
                                    }}
                                >
                                    <Image
                                        src={getLinkDefaultVariantImageUrl(
                                            lpLink,
                                        )}
                                        alt={lpLink.title}
                                        fill
                                        sizes="80px"
                                        style={{
                                            objectFit: 'cover',
                                        }}
                                        onLoadingComplete={(img) => {
                                            const {
                                                naturalWidth,
                                                naturalHeight,
                                            } = img
                                            if (
                                                !naturalWidth ||
                                                !naturalHeight
                                            ) {
                                                return
                                            }
                                            if (
                                                Math.abs(
                                                    naturalWidth -
                                                        naturalHeight,
                                                ) < 8
                                            ) {
                                                setThumbOrientation('square')
                                            } else if (
                                                naturalHeight > naturalWidth
                                            ) {
                                                setThumbOrientation('portrait')
                                            } else {
                                                setThumbOrientation('landscape')
                                            }
                                        }}
                                    />
                                </div>
                                <div
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        justifyContent: 'center',
                                        gap: 4,
                                    }}
                                >
                                    <span
                                        style={{
                                            ...getTypographyRoleStyle(
                                                'productTitle',
                                                {
                                                    fontWeight: 600,
                                                    lineHeight: 1.3,
                                                },
                                            ),
                                            fontSize: scaledFontSize(13),
                                            color: '#ffffff',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {lpLink.title}
                                    </span>
                                    {reviewCount != null &&
                                        reviewRating != null &&
                                        reviewCount > 0 && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    marginTop: 0,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                    }}
                                                >
                                                    <ReviewsStars
                                                        rating={reviewRating}
                                                        count={reviewCount}
                                                        showRating={false}
                                                        starSize={12}
                                                        fontSize={0}
                                                        starColor="#ffffff"
                                                        textColor="#ffffff"
                                                    />
                                                    <span
                                                        style={{
                                                            ...getTypographyRoleStyle(
                                                                'captionText',
                                                                {
                                                                    fontWeight: 400,
                                                                },
                                                            ),
                                                            fontSize:
                                                                scaledFontSize(
                                                                    11,
                                                                ),
                                                            color: '#ffffff',
                                                            opacity: 0.85,
                                                        }}
                                                    >
                                                        {reviewCount} review
                                                        {reviewCount === 1
                                                            ? ''
                                                            : 's'}
                                                    </span>
                                                </span>
                                            </div>
                                        )}
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-end',
                                            gap: 4,
                                            flexShrink: 0,
                                            marginTop: 4,
                                        }}
                                    >
                                        {/* First line: Discount percentage and Price */}
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                alignSelf: 'flex-start',
                                            }}
                                        >
                                            {discountPercentage != null &&
                                                discountPercentage > 0 && (
                                                    <DiscountBadge
                                                        style={{
                                                            marginRight: 4,
                                                        }}
                                                    >
                                                        -{discountPercentage}%
                                                    </DiscountBadge>
                                                )}
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 0,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        ...getTypographyRoleStyle(
                                                            'price',
                                                            { fontWeight: 400 },
                                                        ),
                                                        fontSize:
                                                            scaledFontSize(10),
                                                        color: '#ffffff',
                                                        marginTop: -1,
                                                    }}
                                                >
                                                    {landingPage?.client
                                                        ?.currencySymbol || '$'}
                                                </span>
                                                <PriceDollars
                                                    style={{
                                                        ...getTypographyRoleStyle(
                                                            'price',
                                                            {
                                                                fontWeight: 500,
                                                                lineHeight: 1,
                                                            },
                                                        ),
                                                        fontSize:
                                                            scaledFontSize(17),
                                                        color: '#ffffff',
                                                    }}
                                                >
                                                    {dollars}
                                                </PriceDollars>
                                                <span
                                                    style={{
                                                        ...getTypographyRoleStyle(
                                                            'price',
                                                            { fontWeight: 400 },
                                                        ),
                                                        fontSize:
                                                            scaledFontSize(10),
                                                        color: '#ffffff',
                                                        marginTop: -1,
                                                    }}
                                                >
                                                    {cents}
                                                </span>
                                            </div>
                                        </div>
                                        {/* Second line: List Price: Strikeout price */}
                                        {/* Only show compareAtPrice if there's an actual discount (price comparison) */}
                                        {compareAtPrice &&
                                            priceInfo.compareAtPrice !== null &&
                                            priceInfo.compareAtPrice >
                                                priceInfo.price && (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 4,
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            ...getTypographyRoleStyle(
                                                                'captionText',
                                                                {
                                                                    fontWeight: 400,
                                                                },
                                                            ),
                                                            fontSize:
                                                                scaledFontSize(
                                                                    12,
                                                                ),
                                                            color: '#ffffff',
                                                            opacity: 0.8,
                                                        }}
                                                    >
                                                        List Price:
                                                    </span>
                                                    <ListPrice
                                                        style={{
                                                            color: '#ffffff',
                                                            opacity: 0.8,
                                                        }}
                                                    >
                                                        {compareAtPrice}
                                                    </ListPrice>
                                                </div>
                                            )}
                                    </div>
                                </div>
                            </div>
                        </VideoProductCard>
                    </LPBottomControlsDiv>
                )}
            </BottomControlsContainer>
        </BottomControlsOuterContainer>
    )
}

const BrandAvatarButton = styled.button({
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: 'none',
    padding: 0,
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
    cursor: 'pointer',
})

const NavCircleButton = styled.button({
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: 'none',
    padding: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    position: 'relative',
    verticalAlign: 'middle',
})

const VideoProductCard = styled.div<{ expanded: boolean }>(({ expanded }) => ({
    // Let the card flex so controls never get pushed off-screen on small widths
    width: 'auto',
    flex: 1,
    minWidth: 0,
    borderRadius: '0.2rem',
    padding: '10px',
    // Even lighter opacity so underlying video reads more like the main surface
    backgroundColor: 'rgba(0,0,0,0.5)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
    cursor: 'pointer',
    boxSizing: 'border-box',
    textAlign: 'left',
    transform: expanded ? 'translateY(0)' : 'translateY(110%)',
    opacity: expanded ? 1 : 0,
    transition: 'transform 400ms ease-out, opacity 400ms ease-out',
}))

const BrandRowContainer = styled.div<{ isExpanded: boolean }>(
    ({ isExpanded }) => ({
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 8,
        paddingLeft: 16,
        paddingRight: 16,
        marginBottom: 0,
        pointerEvents: 'auto',
        opacity: isExpanded ? 1 : 0,
        transform: isExpanded ? 'translateY(0)' : 'translateY(-12px)',
        transition: 'opacity 400ms ease-out, transform 400ms ease-out',
        order: 2,
    }),
)
